/**
 * HappeningMap — "Happening Around the Sound" home-page map.
 * This week's events (venue-clustered pins).
 * Lazy: fetches + renders Leaflet only when scrolled into view.
 */
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, Polyline, CircleMarker, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import axios from "axios";
import { Calendar as CalendarIcon, ChevronRight, Anchor, Gift, Gem, Info, Shield } from "lucide-react";
import { absUrl } from "./blocks/media";
import { API } from "../App";
import { NightMarkers, BeforeYouGoOut, NightLifeButton, useNightData, NIGHT_INK, NIGHT_KIND_META } from "./NightLayer";
import { teardropIcon } from "./admin/PinNudgeMap";
import { DayLayerChips, DayPlaceMarkers, useDayPlaces } from "./DayPlaces";
import { HoardChest } from "./hoard/GemHoardTile";
import { HoardMarkers } from "./hoard/HoardMarkers";
import { KrakonMarker } from "./krakon/KrakonMarker";
import { DropSpotMarkers, useDropSpots } from "./dropspots/DropSpotMarkers";
import { DropSpotDock } from "./dropspots/DropSpotDock";
import { DirectionsActions } from "./DirectionsActions";
import { VenuePopupHeader } from "./VenuePopupHeader";
import { RegionBar } from "../regions/RegionBar";
import { RegionFilterEditor } from "../regions/RegionFilterEditor";
import { useRegions, useRegionSelection, inRegion } from "../regions/useRegion";

const CATEGORY_META = {
  "live-music":           { color: "#2E86AB", emoji: "🎵", label: "Live Music" },
  "karaoke":              { color: "#9B59B6", emoji: "🎤", label: "Karaoke" },
  "open-mic":             { color: "#E67E22", emoji: "🎙️", label: "Open Mic" },
  "art-show":             { color: "#C0392B", emoji: "🎨", label: "Art Show" },
  "community-fundraiser": { color: "#27AE60", emoji: "💚", label: "Community / Fundraiser" },
  "food-drink":           { color: "#E57C23", emoji: "🍽️", label: "Food & Drink" },
  "markets-popups":       { color: "#5B8FB9", emoji: "🛍️", label: "Markets & Pop-ups" },
  "workshops-classes":    { color: "#5C7C6E", emoji: "📚", label: "Workshops" },
};
const getMeta = (cat) => CATEGORY_META[cat] || { color: "#5C5C5C", emoji: "📌", label: cat || "Event" };

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

const ZoomWatcher = ({ onZoom }) => {
  const map = useMapEvents({ zoomend: () => onZoom(map.getZoom()) });
  useEffect(() => { onZoom(map.getZoom()); }, [map, onZoom]);
  return null;
};

const iconCache = {};
const buildEventIcon = (category, count, logoUrl) => {
  const meta = getMeta(category);
  const key = `ev-${category}-${count > 1 ? count : 1}-${logoUrl || ""}`;
  if (iconCache[key]) return iconCache[key];
  const badge = count > 1
    ? `<span style="position:absolute;top:-4px;right:-4px;background:#1A1A1A;color:#fff;font-size:11px;line-height:18px;height:18px;min-width:18px;padding:0 5px;border-radius:9px;font-weight:700;border:2px solid white;font-family:-apple-system,sans-serif">${count}</span>`
    : "";
  if (logoUrl) {
    // Logo-only teardrop: white drop with a thin neutral edge, the venue logo fills it (no category color)
    iconCache[key] = L.divIcon({
      className: "happening-event-marker happening-logo-marker",
      html: `<div style="position:relative;width:46px;height:46px">
        <div style="background:#fff;width:46px;height:46px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:2px solid rgba(26,26,26,0.35);box-shadow:0 3px 10px rgba(0,0,0,0.28);display:flex;align-items:center;justify-content:center">
          <div style="transform:rotate(45deg);width:38px;height:38px;border-radius:50%;background:#fff;overflow:hidden;display:flex;align-items:center;justify-content:center">
            <img src="${absUrl(logoUrl)}" alt="" style="width:100%;height:100%;object-fit:cover;display:block" loading="lazy"/>
          </div>
        </div>${badge}
      </div>`,
      iconSize: [46, 46], iconAnchor: [23, 46], popupAnchor: [0, -46],
    });
    return iconCache[key];
  }
  iconCache[key] = L.divIcon({
    className: "happening-event-marker",
    html: `<div style="position:relative;width:36px;height:36px">
      <div style="background:${meta.color};width:36px;height:36px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);display:flex;align-items:center;justify-content:center;border:3px solid white;box-shadow:0 3px 10px rgba(0,0,0,0.3)">
        <span style="transform:rotate(45deg);font-size:16px">${meta.emoji}</span>
      </div>${badge}
    </div>`,
    iconSize: [36, 36], iconAnchor: [18, 36], popupAnchor: [0, -36],
  });
  return iconCache[key];
};

const porchfestIcon = () => {
  if (iconCache.porchfest) return iconCache.porchfest;
  iconCache.porchfest = L.divIcon({
    className: "happening-porchfest-marker",
    html: `<img src="/porchfest/porchfest-logo.png" alt="Porchfest" style="width:46px;height:46px;border-radius:50%;border:3px solid #E76F51;box-shadow:0 3px 12px rgba(0,0,0,0.4);object-fit:cover;background:#fff"/>`,
    iconSize: [46, 46], iconAnchor: [23, 46], popupAnchor: [0, -46],
  });
  return iconCache.porchfest;
};

const isPorchfestVenue = (m) => (m.events || []).some((ev) => /porchfest/i.test(ev.title || ""));

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString([], { weekday: "short", month: "short", day: "numeric", timeZone: "America/Los_Angeles" });
  } catch { return ""; }
};
const formatTime = (iso) => {
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit", timeZone: "America/Los_Angeles" });
  } catch { return ""; }
};

// Idempotent: computes the target view from the points every time, so re-running (re-renders, data refresh) never compounds the zoom-out.
const FitBounds = ({ points, fallback, zoomOut = 0, bounds = null, padBottom = 8 }) => {
  const map = useMap();
  const fbKey = fallback ? `${fallback.center[0]},${fallback.center[1]},${fallback.zoom}` : "";
  const bKey = bounds ? bounds.flat().join(",") : "";
  useEffect(() => {
    if (bounds) { map.fitBounds(L.latLngBounds(bounds), { animate: false, paddingTopLeft: L.point(8, 8), paddingBottomRight: L.point(8, padBottom) }); return; }
    if (points.length > 1) {
      const b = L.latLngBounds(points);
      map.setView(b.getCenter(), map.getBoundsZoom(b, false, L.point(40, 40)) - zoomOut, { animate: false });
    }
    else if (points.length === 1) map.setView(points[0], 12 - zoomOut, { animate: false });
    else if (fallback) map.setView(fallback.center, fallback.zoom - zoomOut, { animate: false });
  }, [points, fbKey, bKey, map, zoomOut, padBottom]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
};

// Locked landing view (user, 2026-10-27 — keep until they ask to change it): Gig Harbor ↔ Tacoma/Fife with the Krakens in the Narrows,
// framed ABOVE the HOARD chest (and the phone buttons) so no pins hide under it. Picking an area chip still fits that area.
const SEASON_BOUNDS = [[47.2367, -122.662], [47.3607, -122.362]];
const chestPad = () => (window.innerWidth < 640 ? 140 : Math.round(Math.min(330, Math.max(176, window.innerWidth * 0.48)) * 0.39) + 14);

const NO_BIZ = [];
// After 7pm Pacific (until 5am) the map opens in Night Life mode — that's when people are deciding where to go.
const isNightNow = () => {
  try {
    const h = Number(new Intl.DateTimeFormat("en-US", { hour: "numeric", hour12: false, timeZone: "America/Los_Angeles" }).format(new Date()));
    return h >= 19 || h < 5;
  } catch { return false; }
};

// `typeTabs` (+ `typeSel`/`onTypeSel`) lets a page (the Full Area Calendar) drive the type pills with its own category list.
const HappeningMap = ({ height = "460px", initialMode, typeTabs, typeSel: extTypeSel, onTypeSel }) => {
  const navigate = useNavigate();
  const wrapRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [venues, setVenues] = useState([]);
  const [allEvents, setAllEvents] = useState([]);
  const [zoom, setZoom] = useState(12);
  const [customPins, setCustomPins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dropSpotDock, setDropSpotDock] = useState(null); // spot_id or null
  const dropSpots = useDropSpots();

  // Defer fetching + Leaflet mount until the section scrolls near the viewport
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) { setVisible(true); io.disconnect(); } },
      { rootMargin: "300px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!visible) return;
    Promise.all([
      axios.get(`${API}/events/map-markers`).catch(() => ({ data: [] })),
      axios.get(`${API}/custom-pins`).catch(() => ({ data: [] })),
      axios.get(`${API}/events`).catch(() => ({ data: [] })),
    ]).then(([evRes, pinRes, allRes]) => {
      setVenues(Array.isArray(evRes.data) ? evRes.data : []);
      setCustomPins(Array.isArray(pinRes.data) ? pinRes.data : []);
      setAllEvents(Array.isArray(allRes.data) ? allRes.data : []);
    }).finally(() => setLoading(false));
  }, [visible]);

  // Time window — "all" upcoming by default; chips narrow to this week / next 2 weeks
  const RANGES = { week: ["This week", 7], two: ["Next 2 weeks", 14], all: ["All upcoming", 3650] };
  const [range, setRange] = useState("all");
  const inWindow = (iso) => {
    const t = new Date(iso).getTime();
    return t >= Date.now() - 12 * 3600 * 1000 && t <= Date.now() + RANGES[range][1] * 24 * 3600 * 1000;
  };
  const inThisWeek = inWindow;
  // Event-type groups shown as filter pills
  const TYPE_GROUPS = {
    music: { label: "Music & Nightlife", cats: ["live-music", "karaoke", "open-mic"] },
    food: { label: "Food & Drink", cats: ["food-drink"] },
    arts: { label: "Arts & Markets", cats: ["art-show", "markets-popups", "workshops-classes"] },
    community: { label: "Community & Orgs", cats: ["community-fundraiser"] },
  };
  const [ownTypeSel, setOwnTypeSel] = useState("all");
  const typeSel = typeTabs ? (extTypeSel || "all") : ownTypeSel;
  const setTypeSel = typeTabs ? (k) => onTypeSel?.(k === "all" ? null : k) : setOwnTypeSel;
  const inType = (ev) => typeSel === "all" || (typeTabs ? ev.category === typeSel : (TYPE_GROUPS[typeSel]?.cats || []).includes(ev.category));
  const typeLabel = (k) => (typeTabs ? typeTabs[k]?.label : TYPE_GROUPS[k]?.label) || k;
  // Map modes: "week" (this week's events) · "hop" (Holiday HOPE Hop Gems) · "giving" (every Gem business = giving locations) · "night" (open after 8pm)
  const [mode, setMode] = useState(initialMode || (isNightNow() ? "night" : "week"));
  const night = mode === "night";
  const [nightDays, setNightDays] = useState(3);
  const nightData = useNightData(night, nightDays);
  const hopOnly = mode !== "week";
  const [hopBiz, setHopBiz] = useState([]);
  const [givingBiz, setGivingBiz] = useState([]);
  useEffect(() => {
    if (mode === "hop" && !hopBiz.length) {
      axios.get(`${API}/holiday-hop`).then((r) => setHopBiz((r.data?.businesses || []).filter((b) => b.lat && b.lng).map((b) => ({ ...b, gem: b.headline_gem?.title || b.headline || "" })))).catch(() => {});
    }
    if (mode === "giving" && !givingBiz.length) {
      axios.get(`${API}/passport/preview`).then((r) => {
        const seen = {};
        for (const t of r.data?.tiles || []) {
          if (typeof t.business_lat !== "number" || seen[t.business_id]) continue;
          seen[t.business_id] = { business_id: t.business_id, name: t.business_name, logo_url: t.business_logo, lat: t.business_lat, lng: t.business_lng, gem: t.title, hop_level: "" };
        }
        setGivingBiz(Object.values(seen));
      }).catch(() => {});
    }
  }, [mode]); // eslint-disable-line react-hooks/exhaustive-deps
  const gemBiz = mode === "hop" ? hopBiz : mode === "giving" ? givingBiz : NO_BIZ;
  const scrollToMap = () => wrapRef.current?.querySelector("[data-testid='happening-map-leaflet']")?.scrollIntoView({ behavior: "smooth", block: "center" });
  const showGiving = () => { setMode((m) => (m === "giving" ? "week" : "giving")); scrollToMap(); };
  const showNight = () => { setMode((m) => (m === "night" ? "week" : "night")); scrollToMap(); };
  const nightPoints = useMemo(() => !nightData ? [] : [...nightData.venues, ...nightData.businesses.filter((b) => !b.auto), ...nightData.spots].map((p) => [p.lat, p.lng]), [nightData]);
  const nightCount = nightData ? nightData.venues.length + nightData.businesses.length : 0;
  // Daytime place layers (coffee / eat / beer / shops / art) — off until tapped; pins sit on top of the event pins.
  const [dayKinds, setDayKinds] = useState([]);
  const dayData = useDayPlaces(dayKinds.length > 0);
  useEffect(() => {
    const h = (e) => { window.__wallPendingPin = e.detail?.business_id;  // the Hoard badge layer (HoardMarkers) flies to it in whichever mode the map is in
      const scroll = () => document.querySelector('[data-testid="happening-map-leaflet"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
      scroll(); setTimeout(scroll, 700);
    };
    window.addEventListener("wall:open-business-pin", h);
    return () => window.removeEventListener("wall:open-business-pin", h);
  }, []);
  const dayCount = useMemo(() => (dayData?.places || []).filter((p) => p.kinds.some((k) => dayKinds.includes(k))).length, [dayData, dayKinds]);

  // THE WALL HOARD — the chest on the map opens the Hoard dock (HoardDock.jsx)

  // Region → City filter (shared with /events; remembered per device)
  const regions = useRegions();
  const [regionSel, setRegionSel] = useRegionSelection();
  const weekEvents = useMemo(() => allEvents.filter((ev) => inThisWeek(ev.start_datetime) && inType(ev)), [allEvents, range, typeSel]); // eslint-disable-line react-hooks/exhaustive-deps
  const upcomingEvents = useMemo(() => allEvents.filter((ev) => new Date(ev.start_datetime).getTime() >= Date.now() - 12 * 3600 * 1000), [allEvents]);
  const activeRegion = regionSel.region !== "all" ? regions.find((r) => r.id === regionSel.region) : null;

  const thisWeekVenues = useMemo(() => {
    if (hopOnly) return [];
    const list = venues
      .map((v) => {
        const events = (v.events || []).filter((ev) => inThisWeek(ev.start_datetime) && inType(ev) && inRegion({ ...ev, city: v.city }, regionSel, regions))
          .sort((a, b) => new Date(a.start_datetime) - new Date(b.start_datetime));
        if (events.length === 0) return null;
        const catCounts = {};
        events.forEach((ev) => { catCounts[ev.category] = (catCounts[ev.category] || 0) + 1; });
        const primary = Object.keys(catCounts).sort((a, b) => catCounts[b] - catCounts[a])[0];
        return { ...v, events, event_count: events.length, primary_category: primary };
      })
      .filter(Boolean);
    // Pins that would overlap on screen (< 52px apart at this zoom) get nudged 56px sideways,
    // alternating west/east, with a leader line back to the venue's true spot.
    const mPerPx = (156543.03 * Math.cos((47.25 * Math.PI) / 180)) / Math.pow(2, zoom);
    const degPerPx = 360 / (256 * Math.pow(2, zoom));
    let flip = 0;
    for (let i = 0; i < list.length; i++) {
      for (let j = 0; j < i; j++) {
        const a = list[i], b = list[j];
        const dLat = (a.lat - (b.pinLat ?? b.lat)) * 111000;
        const dLng = (a.lng - (b.pinLng ?? b.lng)) * 75500;
        if (Math.hypot(dLat, dLng) / mPerPx < 52 && a.pinLat == null) {
          a.pinLat = a.lat;
          a.pinLng = a.lng + (flip++ % 2 === 0 ? -1 : 1) * 56 * degPerPx;
        }
      }
    }
    return list;
  }, [venues, zoom, hopOnly, regionSel, regions, range, typeSel]); // eslint-disable-line react-hooks/exhaustive-deps

  const eventCount = thisWeekVenues.reduce((a, v) => a + v.event_count, 0);
  const weekTotal = useMemo(() => weekEvents.filter((ev) => inRegion(ev, regionSel, regions)).length, [weekEvents, regionSel, regions]);
  const moreOnCalendar = Math.max(0, weekTotal - eventCount);
  const fitPoints = useMemo(() => (night ? nightPoints : hopOnly ? gemBiz.map((b) => [b.lat, b.lng]) : thisWeekVenues.map((v) => [v.lat, v.lng])), [thisWeekVenues, hopOnly, gemBiz, night, nightPoints]);

  return (
    <>
    <div ref={wrapRef} data-testid="happening-map" className={night ? "night-map rounded-2xl p-3 -mx-3" : ""} style={night ? { background: NIGHT_INK, transition: "background-color .4s" } : undefined}>
      {/* Header */}
      <div className="mb-3 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className={`font-['Playfair_Display'] text-2xl font-semibold ${night ? "text-white" : "text-[#1A1A1A]"}`} data-testid="happening-map-title">
            {night ? <>Night Life <span className="ml-1 inline-block align-middle rounded-md bg-[#54E3FF] text-[#120A24] px-2 py-0.5 font-sans text-xs sm:text-sm font-black uppercase tracking-[0.2em]" data-testid="happening-map-night-tag">{nightDays === 1 ? "Tonight" : `Next ${nightDays} nights`}</span></>
              : <>Where the Sound Lives <span className="ml-1 inline-block align-middle rounded-md bg-[#1A1A1A] text-white px-2 py-0.5 font-sans text-xs sm:text-sm font-black uppercase tracking-[0.2em]" data-testid="happening-map-this-week-tag">{RANGES[range][0]}</span></>}
          </h2>
          {night ? (
            <p className="text-[#9CA3AF] text-sm" data-testid="happening-map-night-sub">
              Open after 7pm — venues with shows running past 7 tonight, late kitchens, karaoke &amp; open mics, sober-friendly spots and the safe-night layer (rides, 24h pharmacies, ERs). {nightCount} place{nightCount === 1 ? "" : "s"} {nightDays === 1 ? "tonight" : `over the next ${nightDays} nights`}.
              Hours are best-effort: tap <Info className="inline h-3 w-3 text-[#54E3FF]" /> on any pin to see what's verified.
            </p>
          ) : (
          <p className="text-[#5C5C5C] text-sm">
            Every pin is a place with something on — <b className="text-[#1A1A1A]">{RANGES[range][0].toLowerCase()}</b>, all around the Sound. Tap a pin for its dates, narrow by area or type below the map, or open the full{" "}
            <Link to="/events" className="text-[#2E5C55] font-semibold underline underline-offset-2 hover:text-[#1F3F3A]" data-testid="happening-map-calendar-inline-link">
              Full Area Calendar HERE
            </Link>
            .
          </p>
          )}
        </div>
        <NightLifeButton active={night} onClick={showNight} className="self-start" />
      </div>

      {/* Map */}
      <div className={`rounded-xl overflow-hidden border relative z-0 ${night ? "border-[#54E3FF]/40" : "border-[#E2E0DC]"}`} style={{ height }} data-testid="happening-map-leaflet">
        {!visible || loading ? (
          <div className="flex items-center justify-center bg-[#E8F0EC] h-full">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2E5C55]"></div>
          </div>
        ) : (
          <>
          <HoardChest night={night} />
          {night ? <BeforeYouGoOut spots={nightData?.spots || []} /> : (
          <>
          <Link to="/give" className="absolute bottom-[6.5rem] sm:bottom-8 right-3 z-[500] inline-flex items-center gap-1.5 rounded-full bg-[#1d4ed8] text-white text-[11px] sm:text-xs font-extrabold uppercase tracking-wide px-3.5 py-2 shadow-lg hover:bg-[#1e40af] no-underline" data-testid="happening-map-giving-map-btn">
            <Gift className="h-3.5 w-3.5" /> Holidays Giving Map
          </Link>
          <Link to="/safety" className="absolute bottom-[6.5rem] sm:bottom-8 left-3 z-[500] inline-flex items-center gap-1.5 rounded-full bg-[#B91C1C] text-white text-[11px] sm:text-xs font-extrabold uppercase tracking-wide px-3.5 py-2 shadow-lg hover:bg-[#991B1B] no-underline" data-testid="happening-map-drink-spiking-btn">
            <Shield className="h-3.5 w-3.5" /> Drink Spiking
          </Link>
          </>
          )}
          <MapContainer center={[47.2529, -122.4443]} zoom={10} scrollWheelZoom={true} style={{ height: "100%", width: "100%", background: night ? NIGHT_INK : undefined }}>
            {night ? <NightMarkers data={nightData} days={nightDays} /> : (
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            />
            )}
            {!activeRegion ? <FitBounds points={NO_BIZ} bounds={SEASON_BOUNDS} padBottom={chestPad()} />
              : (fitPoints.length > 0 || activeRegion) && <FitBounds points={fitPoints} fallback={activeRegion ? { center: activeRegion.center, zoom: activeRegion.zoom } : null} zoomOut={night ? 2 : 0} />}
            <ZoomWatcher onZoom={setZoom} />

            {/* Leader lines: a pin nudged off a neighbour points back to its true spot */}
            {thisWeekVenues.filter((m) => m.pinLat != null).map((m, idx) => (
              <Fragment key={`lead-${idx}`}>
                <Polyline positions={[[m.pinLat, m.pinLng], [m.lat, m.lng]]} pathOptions={{ color: "#7C3AED", weight: 2, dashArray: "2 6", opacity: 0.9 }} />
                <CircleMarker center={[m.lat, m.lng]} radius={4} pathOptions={{ color: "#7C3AED", fillColor: "#fff", fillOpacity: 1, weight: 2 }} />
              </Fragment>
            ))}

            {thisWeekVenues.map((m, idx) => (
              <Marker
                key={`v-${m.lat}-${m.lng}-${idx}`}
                position={[m.pinLat ?? m.lat, m.pinLng ?? m.lng]}
                icon={isPorchfestVenue(m) ? porchfestIcon() : buildEventIcon(m.primary_category, m.event_count, m.logo_url)}
                zIndexOffset={isPorchfestVenue(m) ? 1000 : 0}
              >
                <Popup maxWidth={320} minWidth={250}>
                  <div className="min-w-[230px]">
                    {isPorchfestVenue(m) && (
                      <Link to="/porchfest" className="block mb-2 p-2 rounded-lg bg-[#E76F51]/10 hover:bg-[#E76F51]/20 no-underline transition-colors" data-testid="happening-porchfest-intro">
                        <div className="text-[12px] font-bold text-[#E76F51] leading-tight">Tacoma Porch Fest — July 18 &amp; 19</div>
                        <div className="text-[11px] text-[#1A1A1A] leading-snug mt-0.5">
                          Click here for full event schedule, highlights and more - FREE event,
                          all ages - be sure to look at the North Star Guide and enjoy a fun, safe event!
                        </div>
                      </Link>
                    )}
                    <VenuePopupHeader venue={m} testId="happening-venue-header" />
                    <div className="border-t border-[#E2E0DC] pt-2 max-h-44 overflow-y-auto">
                      <p className="text-[10px] font-semibold text-[#5C5C5C] uppercase tracking-wider m-0 mb-1.5">
                        {RANGES[range][0]} ({m.event_count}){m.event_count > 6 ? " · next 6 shown" : ""}
                      </p>
                      <div className="space-y-1.5">
                        {m.events.slice(0, 6).map((ev) => {
                          const meta = getMeta(ev.category);
                          return (
                            <Link
                              key={ev.event_id}
                              to={/porchfest/i.test(ev.title || "") ? "/porchfest" : `/event/${ev.event_id}`}
                              className="block p-1.5 rounded-lg hover:bg-[#F9F8F6] transition-colors no-underline"
                              data-testid={`happening-event-link-${ev.event_id}`}
                            >
                              <div className="flex items-start gap-2">
                                <span className="text-base leading-none mt-0.5" style={{ color: meta.color }}>{meta.emoji}</span>
                                <div className="flex-1 min-w-0">
                                  <div className="text-[12px] font-semibold text-[#1A1A1A] truncate leading-tight">{ev.title}</div>
                                  <div className="text-[10px] text-[#5C5C5C]">
                                    {formatDate(ev.start_datetime)} · {formatTime(ev.start_datetime)}
                                    {ev.cover_charge ? ` · ${ev.cover_charge}` : ""}
                                  </div>
                                </div>
                                <ChevronRight className="h-3 w-3 text-[#9C9C9C] mt-1 flex-shrink-0" />
                              </div>
                              {ev.host_type === "artist" && ev.host_slug && (
                                <span
                                  role="link"
                                  tabIndex={0}
                                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate(`/artists/${ev.host_slug}`); }}
                                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.stopPropagation(); navigate(`/artists/${ev.host_slug}`); } }}
                                  className="mt-1 ml-6 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#B8860B] hover:underline"
                                  data-testid={`happening-host-link-${ev.event_id}`}
                                >
                                  Learn about your host{ev.host_name ? ` · ${ev.host_name}` : ""} →
                                </span>
                              )}
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                    <div className="border-t border-[#E2E0DC] mt-2 pt-2">
                      <DirectionsActions variant="compact" size="xs" lat={m.lat} lng={m.lng} address={m.address || m.full_address || ""} testId={`happening-directions-${idx}`} />
                    </div>
                    <Link
                      to={`/events?venue=${encodeURIComponent(m.venue_name || "")}&view=list`}
                      className="mt-2 flex items-center justify-center gap-1.5 rounded-full !bg-[#1A1A1A] !text-white text-[11px] font-black tracking-wide px-3 py-2 no-underline hover:!bg-[#2E5C55]"
                      data-testid={`happening-full-calendar-${idx}`}
                    >
                      See {m.event_count > 6 ? `all ${m.event_count} dates` : "full calendar"}{m.venue_name ? ` · ${m.venue_name}` : ""} <ChevronRight className="h-3 w-3" />
                    </Link>
                  </div>
                </Popup>
              </Marker>
            ))}

            {hopOnly && gemBiz.map((b) => (
              <Marker key={`gem-${b.business_id}`} position={[b.lat, b.lng]} icon={buildEventIcon("gem", 1, b.logo_url)} zIndexOffset={800}>
                <Popup>
                  <div className="text-sm min-w-[180px]">
                    <div className="font-bold text-[#1A1A1A]">{b.name}</div>
                    {b.gem && <div className="text-xs text-[#1A1A1A] mt-0.5">💎 {b.gem}</div>}
                    {b.hop_level && <div className="text-[10px] font-bold uppercase tracking-wide text-[#B8860B]">{b.hop_level} · Holiday HOPE Hop</div>}
                    <Link to={mode === "hop" ? "/holiday-hop" : "/passport"} className="text-xs underline text-[#1d4ed8]">{mode === "hop" ? "See the Gem →" : "Unlock it in your HOARD →"}</Link>
                  </div>
                </Popup>
              </Marker>
            ))}
            {!hopOnly && dayKinds.length > 0 && <DayPlaceMarkers data={dayData} sel={dayKinds} />}
            {/* THE WALL HOARD badges — every Gem provider, in every map mode */}
            <HoardMarkers night={night} />
            {/* THE KRAKON — surfaces somewhere new each day */}
            <KrakonMarker />
            {/* Neighborhood drop spots — mailbox pins */}
            {!night && <DropSpotMarkers spots={dropSpots} onTap={(id) => setDropSpotDock(id)} />}
            {!hopOnly && customPins.map((p) => (
              <Marker
                key={p.pin_id}
                position={[p.lat, p.lng]}
                icon={teardropIcon(p)}
                zIndexOffset={900}
              >
                <Popup maxWidth={280} minWidth={200}>
                  <div className="min-w-[190px]" data-testid={`custom-pin-popup-${p.pin_id}`}>
                    <h3 className="font-bold text-[14px] text-[#1A1A1A] m-0 mb-1 leading-tight">{p.title}</h3>
                    {p.blurb && <p className="text-[12px] text-[#5C5C5C] m-0 mb-2 leading-snug">{p.blurb}</p>}
                    {p.link_url && (
                      p.link_url.startsWith("/") ? (
                        <Link to={p.link_url} className="text-[12px] font-semibold text-[#2E5C55] hover:underline no-underline">
                          Learn more →
                        </Link>
                      ) : (
                        <a href={p.link_url} target="_blank" rel="noopener noreferrer" className="text-[12px] font-semibold text-[#2E5C55] hover:underline no-underline">
                          Learn more →
                        </a>
                      )
                    )}
                  </div>
                </Popup>
              </Marker>
            ))}

          </MapContainer>
          </>
        )}
        {visible && !loading && !hopOnly && thisWeekVenues.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/70 pointer-events-none z-[500]">
            <div className="text-center bg-white border border-[#E2E0DC] rounded-2xl shadow-lg p-5 max-w-xs">
              <CalendarIcon className="h-8 w-8 text-[#E2E0DC] mx-auto mb-2" />
              <p className="text-sm text-[#5C5C5C] m-0">Quiet week on the map — check the full calendar for everything coming up.</p>
            </div>
          </div>
        )}
      </div>

      {/* Filters — narrow the pins by area, time window and type */}
      {/* Region → City picker (this week's counts) */}
      {!hopOnly && (
        <div className="mb-3" data-testid="happening-map-region">
          <RegionBar compact events={upcomingEvents} regions={regions} sel={regionSel} setSel={setRegionSel} testId="home-region" extra={<RegionFilterEditor regions={regions} events={upcomingEvents} />} />
        </div>
      )}

      {/* Time window + event-type pills */}
      {!hopOnly && (
        <div className="mb-3 flex flex-wrap gap-1.5 items-center" data-testid="happening-map-filters">
          {Object.entries(RANGES).map(([k, [label]]) => (
            <button key={k} type="button" onClick={() => setRange(k)} data-testid={`happening-range-${k}`}
              className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${range === k ? "bg-[#1A1A1A] text-white" : "bg-white border border-[#E2E0DC] text-[#5C5C5C] hover:border-[#1A1A1A]"}`}>{label}</button>
          ))}
          <span className="mx-1 h-5 w-px bg-[#E2E0DC]" />
          {[["all", "All types"], ...Object.entries(typeTabs || TYPE_GROUPS).map(([k, g]) => [k, g.label, g.icon, g.color])].map(([k, label, Icon, color]) => {
            const on = typeSel === k;
            return (
              <button key={k} type="button" onClick={() => setTypeSel(on && typeTabs ? "all" : k)} data-testid={`happening-type-${k}`}
                className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${on ? "text-white" : "bg-white border border-[#E2E0DC] text-[#5C5C5C] hover:border-[#2E5C55]"}`}
                style={on ? { background: color || "#2E5C55", borderColor: color || "#2E5C55" } : undefined}>
                {Icon && <Icon className="h-3.5 w-3.5" style={on ? undefined : { color }} />}{label}
              </button>
            );
          })}
        </div>
      )}

      {/* Daytime place layers — coffee / eat / beer / shops / art, off until tapped */}
      {!hopOnly && <DayLayerChips sel={dayKinds} setSel={setDayKinds} count={dayCount} loading={dayKinds.length > 0 && !dayData} />}

      {/* Layer chips */}
      <div className="mb-3 flex flex-wrap gap-2 items-center">
        {night ? (
          <>
            {[[1, "Tonight"], [3, "Next 3 nights"], [7, "Next 7 nights"]].map(([n, label]) => (
              <button key={n} type="button" onClick={() => setNightDays(n)} data-testid={`happening-night-days-${n}`}
                className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ring-1 ring-[#54E3FF]/50 ${nightDays === n ? "bg-[#54E3FF] text-[#120A24]" : "bg-transparent text-[#54E3FF] hover:bg-white/10"}`}>{label}</button>
            ))}
            <span className="mx-1 h-5 w-px bg-white/20" />
            {["music", "social", "food", "sober", "safe"].map((k) => (
              <span key={k} className="inline-flex items-center gap-1 text-[11px] font-semibold" style={{ color: NIGHT_KIND_META[k].color }} data-testid={`night-legend-${k}`}>{NIGHT_KIND_META[k].emoji} {NIGHT_KIND_META[k].label}</span>
            ))}
            {nightData && nightCount === 0 && <span className="text-xs text-[#9CA3AF]" data-testid="night-empty">Quiet night so far — try the next 3 or 7 nights, or ask us to add a spot.</span>}
          </>
        ) : (
        <>
        <span
          className="inline-flex items-center px-3.5 py-2 rounded-full text-sm font-black uppercase tracking-wider text-white bg-[#2E5C55] shadow-sm"
          data-testid="happening-map-week-chip"
        >
          <CalendarIcon className="h-4 w-4 mr-1.5 inline" /> {RANGES[range][0]}{activeRegion ? ` · ${regionSel.city || activeRegion.label}` : ""}{typeSel !== "all" ? ` · ${typeLabel(typeSel)}` : ""}
          <span className="ml-2 text-[11px] font-semibold normal-case tracking-normal opacity-85">{eventCount} events · {thisWeekVenues.length} venues</span>
        </span>
        {moreOnCalendar > 0 && (
          <Link
            to={regionSel.region !== "all" ? `/events?region=${regionSel.region}${regionSel.city ? `&city=${encodeURIComponent(regionSel.city)}` : ""}` : "/events"}
            className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-bold text-white bg-[#E76F51] hover:bg-[#C0392B] shadow-sm transition-colors no-underline"
            data-testid="happening-map-more-events-chip"
          >
            +{moreOnCalendar} more without a map pin yet — on the Full Area Calendar
            <ChevronRight className="h-3.5 w-3.5 ml-0.5" />
          </Link>
        )}
        <Link
          to="/gig-harbor"
          className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-white text-[#1d4ed8] border border-[#1d4ed8]/40 hover:bg-[#1d4ed8] hover:text-white transition-colors no-underline"
          data-testid="happening-map-gig-harbor-chip"
        >
          <Anchor className="h-3 w-3 mr-1 inline" /> Gig Harbor guide →
        </Link>
        </>
        )}
      </div>

      {!night && (<>
      {/* Give & Let Live — Giving Map banner (full width) */}
      <div className={`mb-3 rounded-2xl border-2 bg-white px-4 pt-3 pb-2.5 transition-colors ${mode === "giving" ? "border-[#1d4ed8]" : "border-[#E2E0DC] hover:border-[#1d4ed8]"}`} data-testid="happening-map-give-banner">
        <button type="button" onClick={showGiving} className="block w-full" aria-pressed={mode === "giving"} title="Show the giving locations on the map" data-testid="happening-map-give-filter">
          <img src="/give-and-let-live.jpg" alt="Give & Let Live — tap to show giving locations on the map" className="w-full h-auto" />
          <span className="mt-1.5 block text-[10px] font-bold uppercase tracking-widest text-[#1d4ed8]">{mode === "giving" ? "Showing the giving locations ✓ · tap to go back to this week" : "Tap the sign → see the giving locations on the map"}</span>
        </button>
        <Link to="/passport" className="mt-1.5 block text-center text-[11px] sm:text-xs font-extrabold uppercase tracking-wide text-[#1A1A1A] no-underline hover:text-[#1d4ed8]" data-testid="happening-map-give-passport-link">Adopt an org &amp; get <span className="text-[#1d4ed8]">free music &amp; local deals</span> — see how to unlock your HOARD →</Link>
      </div>

      {/* Season spotlight: Holiday HOPE Hop (Dec 18–20) → /holiday-hop */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setMode((m) => (m === "hop" ? "week" : "hop"))}
          aria-pressed={mode === "hop"}
          className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-extrabold uppercase tracking-wide border-2 transition-colors ${mode === "hop" ? "bg-[#F2CC8F] text-[#0f2a44] border-[#F2CC8F]" : "bg-white text-[#0f2a44] border-[#F2CC8F] hover:bg-[#FFF8E7]"}`}
          data-testid="happening-map-hop-filter"
        >
          <Gem className="h-3.5 w-3.5" /> {mode === "hop" ? "Showing HOPE Hop Gems only ✓" : "Show HOPE Hop Gems only"}
        </button>
        {hopOnly && <span className="text-xs text-[#5C5C5C]" data-testid="happening-map-hop-count">{gemBiz.length} {mode === "giving" ? "giving location" : "Gem business"}{gemBiz.length === 1 ? "" : (mode === "giving" ? "s" : "es")} · tap a pin for its Gem</span>}
        <Link
          to="/holiday-hop"
          className="inline-flex items-center gap-3 pl-2 pr-4 py-2 rounded-2xl text-xs font-bold text-white bg-[#0f2a44] border-2 border-[#F2CC8F]/60 hover:border-[#F2CC8F] shadow-sm no-underline transition-colors max-w-full"
          data-testid="happening-map-hop-link"
        >
          <img src={absUrl("/api/uploads/art/giving_map_wide_af13ae.jpg")} alt="Holiday HOPE Hop · Stars 4 Peace Giving Map" className="h-11 w-16 rounded-lg object-cover shrink-0 bg-white" />
          <span className="leading-snug">
            <span className="text-[#F2CC8F] uppercase tracking-wider text-[10px] block">Holiday HOPE Hop · South Sound · Dec 18–20</span>
            Give to a community org → unlock Gems: deals, music &amp; art from local businesses
          </span>
        </Link>
      </div>
      </>)}

      {/* Full calendar — everything above plus the dates without a pin yet */}
      <Link
        to="/events"
        className={`mt-1 flex items-center justify-center gap-2 w-full rounded-full text-sm font-black tracking-wide px-5 py-3.5 transition-colors no-underline ${night ? "bg-[#54E3FF] text-[#120A24] hover:bg-[#7eeaff]" : "bg-[#2E5C55] text-white hover:bg-[#1F3F3A]"}`}
        data-testid="happening-map-calendar-cta"
      >
        <CalendarIcon className="h-4 w-4" /> Full Area Calendar →
      </Link>

      {/* Portals */}
      <div className={`mt-4 pt-3 border-t ${night ? "border-white/15" : "border-[#E2E0DC]"}`} data-testid="happening-map-portals">
        <div className={`text-xs uppercase tracking-wider font-medium text-center mb-3 ${night ? "text-[#9CA3AF]" : "text-[#5C5C5C]"}`}>✨ Around the Sound</div>
        <div className="flex flex-wrap gap-2 justify-center">
          <Link
            to="/events/music"
            className="inline-flex items-center gap-2 text-white px-4 py-2 rounded-full text-sm font-semibold transition-all no-underline hover:shadow-lg hover:-translate-y-0.5"
            style={{ backgroundColor: "#9B59B6" }}
            data-testid="happening-map-portal-music"
          >
            <span className="text-base leading-none">🎶</span> Music Portal <span className="text-[10px] opacity-80">→</span>
          </Link>
          <Link
            to="/for-businesses"
            className="inline-flex items-center gap-2 text-white px-4 py-2 rounded-full text-sm font-semibold transition-all no-underline hover:shadow-lg hover:-translate-y-0.5"
            style={{ backgroundColor: "#16A085" }}
            data-testid="happening-map-portal-business"
          >
            <span className="text-base leading-none">🏪</span> Business Portal <span className="text-[10px] opacity-80">→</span>
          </Link>
        </div>
      </div>
    </div>
    {/* Drop Spot dock */}
    {dropSpotDock && <DropSpotDock spots={dropSpots} activeSpotId={dropSpotDock} onClose={() => setDropSpotDock(null)} />}
    </>
  );
};

export default HappeningMap;
